import React, { useEffect, useMemo, useState } from "react";
import API from "../services/api";
import { resolveProfilePhoto as resolveAssetUrl } from "../utils/profilePhoto";
import "./AdminProductCatalog.css";

const emptyTier = () => ({
  rate: "",
});

const defaultForm = () => ({
  name: "",
  targetAudience: "vendor",
  unit: "KG",
  dispatchFormType: "standard",
  gstPercent: "",
  weightValue: "",
  weightUnit: "KG",
  description: "",
  active: true,
  pricingTiers: [emptyTier()],
});

const defaultGalleryForm = () => ({
  productId: "",
  image: null,
  previewUrl: "",
  imageId: "",
  caption: "",
  packagingWeights: [],
  customWeightInput: "",
  isPrimary: false,
  sortOrder: "",
});

const getPrimaryRate = (product) => product?.pricingTiers?.[0]?.rate ?? "";

const normalizeWeightLabel = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/kg/gi, "KG")
    .replace(/gram/gi, "Gram")
    .replace(/g\b/gi, "Gram")
    .replace(/quintal/gi, "Quintal")
    .replace(/ton/gi, "Ton");

const getProductWeightLabel = (product) => {
  if (product?.weightValue === undefined || product?.weightValue === null || product?.weightValue === "") {
    return "";
  }
  return normalizeWeightLabel(`${product.weightValue} ${product.weightUnit || "KG"}`);
};

const AdminProductCatalog = () => {
  const [products, setProducts] = useState([]);
  const [activeAudience, setActiveAudience] = useState("vendor");
  const [form, setForm] = useState(defaultForm());
  const [galleryForm, setGalleryForm] = useState(defaultGalleryForm());
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gallerySaving, setGallerySaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [galleryError, setGalleryError] = useState("");
  const [galleryMessage, setGalleryMessage] = useState("");

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await API.get("/admin/products");
      const nextProducts = Array.isArray(res.data) ? res.data : [];
      setProducts(nextProducts);
      setError("");
      setGalleryError("");
      setGalleryForm((prev) => {
        const hasCurrent = nextProducts.some((item) => item._id === prev.productId);
        if (hasCurrent) return prev;
        return { ...defaultGalleryForm(), productId: nextProducts[0]?._id || "" };
      });
    } catch (err) {
      console.error("Error loading products:", err);
      setError("Failed to load products.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const selectedGalleryProduct = useMemo(
    () => products.find((item) => item._id === galleryForm.productId) || null,
    [galleryForm.productId, products]
  );

  const filteredProducts = useMemo(
    () => products.filter((item) => (item.targetAudience || "vendor") === activeAudience),
    [products, activeAudience]
  );

  const packagingOptions = useMemo(() => {
    const options = new Set(galleryForm.packagingWeights.map(normalizeWeightLabel));
    const selectedWeight = getProductWeightLabel(selectedGalleryProduct);
    if (selectedWeight) options.add(selectedWeight);
    (selectedGalleryProduct?.galleryImages || []).forEach((image) => {
      (image.packagingWeights || []).forEach((weight) => options.add(normalizeWeightLabel(weight)));
    });
    return [...options].filter(Boolean);
  }, [galleryForm.packagingWeights, selectedGalleryProduct]);
  

  const handleField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRateChange = (value) => {
    setForm((prev) => ({ ...prev, pricingTiers: [{ rate: value }] }));
  };

  const resetForm = () => {
    setForm({ ...defaultForm(), targetAudience: activeAudience });
    setEditingId(null);
  };

  const resetGalleryForm = (productId = galleryForm.productId || filteredProducts[0]?._id || "") => {
    setGalleryForm({ ...defaultGalleryForm(), productId });
    setGalleryError("");
  };

  const validateForm = () => {
    if (!form.name.trim()) return "Product name is required.";
    if (!form.unit) return "Unit is required.";

    for (let i = 0; i < form.pricingTiers.length; i += 1) {
      const t = form.pricingTiers[i];
      if (t.rate === "") {
        return "Rate is required.";
      }
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      name: form.name.trim(),
      targetAudience: form.targetAudience,
      unit: form.unit,
      dispatchFormType: form.dispatchFormType,
      gstPercent: form.gstPercent === "" ? 0 : form.gstPercent,
      weightValue: form.weightValue === "" ? undefined : form.weightValue,
      weightUnit: form.weightUnit,
      description: form.description?.trim(),
      active: form.active,
      pricingTiers: form.pricingTiers.map((t) => ({
        minQty: 0,
        rate: t.rate,
        discountPercent: 0,
      })),
    };

    try {
      setSaving(true);
      if (editingId) {
        await API.put(`/admin/products/${editingId}`, payload);
        setMessage("Product updated.");
      } else {
        await API.post("/admin/products", payload);
        setMessage("Product created.");
      }
      resetForm();
      await fetchProducts();
    } catch (err) {
      console.error("Error saving product:", err);
      setError(err.response?.data?.message || "Failed to save product.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (product) => {
    setEditingId(product._id);
    setForm({
      name: product.name || "",
      targetAudience: product.targetAudience || "vendor",
      unit: product.unit || "KG",
      dispatchFormType: product.dispatchFormType || "standard",
      gstPercent: product.gstPercent ?? "",
      weightValue: product.weightValue ?? "",
      weightUnit: product.weightUnit || "KG",
      description: product.description || "",
      active: product.active !== false,
      pricingTiers:
        product.pricingTiers && product.pricingTiers.length > 0
          ? product.pricingTiers.map((t) => ({
              rate: t.rate ?? "",
            }))
          : [emptyTier()],
    });
    setGalleryForm((prev) => ({ ...prev, productId: product._id }));
    setMessage("");
    setError("");
  };

  const toggleActive = async (product) => {
    try {
      await API.put(`/admin/products/${product._id}`, {
        active: !product.active,
      });
      await fetchProducts();
    } catch (err) {
      console.error("Error updating status:", err);
      setError("Failed to update product status.");
    }
  };

  const deleteProduct = async (product) => {
    const ok = window.confirm(`Delete product "${product.name}" permanently?`);
    if (!ok) return;
    try {
      await API.delete(`/admin/products/${product._id}/permanent`);
      setMessage("Product deleted.");
      if (editingId === product._id) {
        resetForm();
      }
      if (galleryForm.productId === product._id) {
        resetGalleryForm("");
      }
      await fetchProducts();
    } catch (err) {
      console.error("Error deleting product:", err);
      setError(err.response?.data?.message || "Failed to delete product.");
    }
  };

  useEffect(() => {
    setForm((prev) => ({ ...prev, targetAudience: activeAudience }));
    setGalleryForm((prev) => {
      const nextProducts = products.filter((item) => (item.targetAudience || "vendor") === activeAudience);
      const hasCurrent = nextProducts.some((item) => item._id === prev.productId);
      return {
        ...defaultGalleryForm(),
        productId: hasCurrent ? prev.productId : nextProducts[0]?._id || "",
      };
    });
    setEditingId((prev) => {
      if (!prev) return null;
      const current = products.find((item) => item._id === prev);
      return current && (current.targetAudience || "vendor") === activeAudience ? prev : null;
    });
  }, [activeAudience, products]);

  const handleGalleryField = (field, value) => {
    setGalleryForm((prev) => ({ ...prev, [field]: value }));
  };

  const addCustomPackagingWeight = () => {
    const nextWeight = normalizeWeightLabel(galleryForm.customWeightInput);
    if (!nextWeight) return;
    setGalleryForm((prev) => ({
      ...prev,
      packagingWeights: prev.packagingWeights.includes(nextWeight)
        ? prev.packagingWeights
        : [...prev.packagingWeights, nextWeight],
      customWeightInput: "",
    }));
  };

  const togglePackagingWeight = (weight) => {
    setGalleryForm((prev) => {
      const exists = prev.packagingWeights.includes(weight);
      return {
        ...prev,
        packagingWeights: exists
          ? prev.packagingWeights.filter((item) => item !== weight)
          : [...prev.packagingWeights, weight],
      };
    });
  };

  const onGalleryFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setGalleryForm((prev) => ({
      ...prev,
      image: file,
      previewUrl: file ? URL.createObjectURL(file) : prev.previewUrl,
    }));
  };

  const startGalleryEdit = (product, image) => {
    const reserved = new Set((image.packagingWeights || []).map(normalizeWeightLabel));

    setGalleryForm({
      productId: product._id,
      image: null,
      previewUrl: resolveAssetUrl(image.imageUrl),
      imageId: image._id,
      caption: image.caption || "",
      packagingWeights: [...reserved],
      customWeightInput: "",
      isPrimary: !!image.isPrimary,
      sortOrder: image.sortOrder ?? "",
    });
    setGalleryError("");
    setGalleryMessage("");
  };

  const buildGalleryPayload = () => {
    const productWeight = getProductWeightLabel(selectedGalleryProduct);
    const mergedWeights = new Set(galleryForm.packagingWeights.map(normalizeWeightLabel));

    if (productWeight && mergedWeights.size === 0) {
      mergedWeights.add(productWeight);
    }

    return {
      packagingWeights: [...mergedWeights],
      caption: galleryForm.caption.trim(),
      isPrimary: !!galleryForm.isPrimary,
      sortOrder: galleryForm.sortOrder === "" ? 0 : galleryForm.sortOrder,
    };
  };

  const handleGallerySubmit = async (event) => {
    event.preventDefault();
    setGalleryError("");
    setGalleryMessage("");

    if (!galleryForm.productId) {
      setGalleryError("Select a product first.");
      return;
    }

    if (!galleryForm.imageId && !galleryForm.image) {
      setGalleryError("Image file is required.");
      return;
    }

    const payload = buildGalleryPayload();
    const formData = new FormData();
    if (galleryForm.image) {
      formData.append("image", galleryForm.image);
    }
    formData.append("caption", payload.caption);
    formData.append("isPrimary", String(payload.isPrimary));
    formData.append("sortOrder", payload.sortOrder);
    formData.append("packagingWeights", JSON.stringify(payload.packagingWeights));

    try {
      setGallerySaving(true);
      if (galleryForm.imageId) {
        await API.put(
          `/admin/products/${galleryForm.productId}/gallery-images/${galleryForm.imageId}`,
          formData
        );
        setGalleryMessage("Gallery image updated.");
      } else {
        await API.post(`/admin/products/${galleryForm.productId}/gallery-images`, formData);
        setGalleryMessage("Gallery image uploaded.");
      }
      resetGalleryForm(galleryForm.productId);
      await fetchProducts();
    } catch (err) {
      console.error("Error saving gallery image:", err);
      setGalleryError(err.response?.data?.message || "Failed to save gallery image.");
    } finally {
      setGallerySaving(false);
    }
  };

  const deleteGalleryImage = async (productId, imageId) => {
    const ok = window.confirm("Delete this gallery image?");
    if (!ok) return;
    try {
      await API.delete(`/admin/products/${productId}/gallery-images/${imageId}`);
      setGalleryMessage("Gallery image deleted.");
      if (galleryForm.imageId === imageId) {
        resetGalleryForm(productId);
      }
      await fetchProducts();
    } catch (err) {
      console.error("Error deleting gallery image:", err);
      setGalleryError(err.response?.data?.message || "Failed to delete gallery image.");
    }
  };

  return (
    <div className="ap-page">
      <div className="ap-hero">
        <div>
          <h2>Product Catalog Management</h2>
          <p>Admin yahin se vendor aur supplier ke products alag tabs me upload aur manage karega.</p>
        </div>
      </div>

      <div className="ap-audience-tabs">
        <button
          type="button"
          className={`ap-audience-tab ${activeAudience === "vendor" ? "active" : ""}`}
          onClick={() => setActiveAudience("vendor")}
        >
          Vendor
        </button>
        <button
          type="button"
          className={`ap-audience-tab ${activeAudience === "supplier" ? "active" : ""}`}
          onClick={() => setActiveAudience("supplier")}
        >
          Supplier
        </button>
      </div>

      <div className="ap-grid">
        <aside className="ap-side">
          <div className="ap-panel">
            <div className="ap-panel-head">
              <h3>Add / Edit Product</h3>
            </div>
            <div className="ap-panel-body">
              {message ? <div className="ap-alert success">{message}</div> : null}
              {error ? <div className="ap-alert danger">{error}</div> : null}

              <form onSubmit={handleSubmit} className="ap-form">
                <label className="ap-field">
                  <span>Audience</span>
                  <input value={form.targetAudience === "vendor" ? "Vendor" : "Supplier"} disabled />
                </label>
                <label className="ap-field">
                  <span>Product Name</span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => handleField("name", e.target.value)}
                    placeholder="e.g. Basmati Rice"
                  />
                </label>

                <div className="ap-row-2">
                  <label className="ap-field">
                    <span>Unit</span>
                    <select value={form.unit} onChange={(e) => handleField("unit", e.target.value)}>
                      <option value="KG">Kilograms (kg)</option>
                      <option value="Ton">Ton</option>
                      <option value="Quintal">Quintal</option>
                      <option value="Litre">Liters (l)</option>
                    </select>
                  </label>
                  <label className="ap-field">
                    <span>Status</span>
                    <select
                      value={form.active ? "active" : "inactive"}
                      onChange={(e) => handleField("active", e.target.value === "active")}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </label>
                </div>

                <label className="ap-field">
                  <span>Audience</span>
                  <input value={form.targetAudience === "vendor" ? "Vendor" : "Supplier"} disabled />
                </label>

                {form.targetAudience === "supplier" ? (
                  <label className="ap-field">
                    <span>Supplier Dispatch Format</span>
                    <select
                      value={form.dispatchFormType}
                      onChange={(e) => handleField("dispatchFormType", e.target.value)}
                    >
                      <option value="standard">Standard Dispatch Form</option>
                      <option value="paddy">Paddy Quality Format</option>
                    </select>
                  </label>
                ) : null}

                <label className="ap-field">
                  <span>GST %</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.gstPercent}
                    onChange={(e) => handleField("gstPercent", e.target.value)}
                    placeholder="0 for no GST"
                  />
                </label>

                <div className="ap-row-2">
                  <label className="ap-field">
                    <span>Default Weight</span>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={form.weightValue}
                      onChange={(e) => handleField("weightValue", e.target.value)}
                      placeholder="Optional"
                    />
                  </label>
                  <label className="ap-field">
                    <span>Weight Unit</span>
                    <select value={form.weightUnit} onChange={(e) => handleField("weightUnit", e.target.value)}>
                      <option value="KG">KG</option>
                      <option value="Gram">Gram</option>
                      <option value="Ton">Ton</option>
                      <option value="Quintal">Quintal</option>
                    </select>
                  </label>
                </div>

                <label className="ap-field">
                  <span>Description</span>
                  <textarea
                    rows={3}
                    value={form.description}
                    onChange={(e) => handleField("description", e.target.value)}
                    placeholder="Brief product description for catalog..."
                  />
                </label>

                <label className="ap-field">
                  <span>Rate (Rs)</span>
                  <input
                    type="number"
                    min="0"
                    value={form.pricingTiers[0]?.rate ?? ""}
                    onChange={(e) => handleRateChange(e.target.value)}
                    placeholder="Enter product rate"
                  />
                </label>

                <div className="ap-form-actions">
                  <button type="submit" className="ap-btn ap-btn-primary" disabled={saving}>
                    {saving ? "Saving..." : editingId ? "Update Product" : "Save Product"}
                  </button>
                  <button type="button" className="ap-btn ap-btn-ghost" onClick={resetForm}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="ap-panel ap-gallery-panel">
            <div className="ap-panel-head">
              <h3>Upload Gallery Image</h3>
            </div>
            <div className="ap-panel-body">
              {galleryMessage ? <div className="ap-alert success">{galleryMessage}</div> : null}
              {galleryError ? <div className="ap-alert danger">{galleryError}</div> : null}

              <form onSubmit={handleGallerySubmit} className="ap-form">
                <label className="ap-field">
                  <span>Product</span>
                  <select
                    value={galleryForm.productId}
                    onChange={(e) => {
                      const nextProductId = e.target.value;
                      setGalleryForm((prev) => ({
                        ...defaultGalleryForm(),
                        productId: nextProductId,
                      }));
                    }}
                  >
                    <option value="">Select product</option>
                    {filteredProducts.map((product) => (
                      <option key={product._id} value={product._id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="ap-field">
                  <span>{galleryForm.imageId ? "Replace Image (optional)" : "Image"}</span>
                  <input type="file" accept="image/*" onChange={onGalleryFileChange} />
                </label>

                {galleryForm.previewUrl ? (
                  <div className="ap-gallery-preview">
                    <img src={galleryForm.previewUrl} alt="Gallery preview" />
                  </div>
                ) : null}

                <label className="ap-field">
                  <span>Caption</span>
                  <input
                    type="text"
                    value={galleryForm.caption}
                    onChange={(e) => handleGalleryField("caption", e.target.value)}
                    placeholder="e.g. 1 KG retail packet front view"
                  />
                </label>

                <div className="ap-field">
                  <span>Packaging Weights</span>
                  <div className="ap-checkbox-grid">
                    {packagingOptions.map((weight) => (
                      <label key={weight} className="ap-checkbox-chip">
                        <input
                          type="checkbox"
                          checked={galleryForm.packagingWeights.includes(weight)}
                          onChange={() => togglePackagingWeight(weight)}
                        />
                        <span>{weight}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="ap-field">
                  <span>Add Packaging Weight Option</span>
                  <div className="ap-inline-input">
                    <input
                      type="text"
                      value={galleryForm.customWeightInput}
                      onChange={(e) => handleGalleryField("customWeightInput", e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addCustomPackagingWeight();
                        }
                      }}
                      placeholder="e.g. 2 KG, 30 KG, 40 KG"
                    />
                    <button
                      type="button"
                      className="ap-btn ap-btn-ghost"
                      onClick={addCustomPackagingWeight}
                    >
                      Add
                    </button>
                  </div>
                </div>

                <label className="ap-checkbox-row">
                  <input
                    type="checkbox"
                    checked={galleryForm.isPrimary}
                    onChange={(e) => handleGalleryField("isPrimary", e.target.checked)}
                  />
                  <span>Is image ko front / primary image banao</span>
                </label>

                <label className="ap-field">
                  <span>Sort Order</span>
                  <input
                    type="number"
                    value={galleryForm.sortOrder}
                    onChange={(e) => handleGalleryField("sortOrder", e.target.value)}
                    placeholder="0"
                  />
                </label>

                <div className="ap-form-actions">
                  <button type="submit" className="ap-btn ap-btn-primary" disabled={gallerySaving}>
                    {gallerySaving ? "Saving..." : galleryForm.imageId ? "Update Image" : "Upload Image"}
                  </button>
                  <button
                    type="button"
                    className="ap-btn ap-btn-ghost"
                    onClick={() => resetGalleryForm()}
                  >
                    Reset
                  </button>
                </div>
              </form>
            </div>
          </div>
        </aside>

        <section className="ap-table-panel">
          <div className="ap-table-head">
            <div className="ap-table-title">
              <span>Catalog List</span>
              <span className="ap-count">{filteredProducts.length} Products</span>
            </div>
            <div className="ap-table-tools">
              <button className="ap-icon-btn" onClick={fetchProducts} title="Refresh list">
                <span className="material-symbols-outlined">refresh</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="ap-empty">Loading products...</div>
          ) : filteredProducts.length === 0 ? (
            <div className="ap-empty">No products added yet.</div>
          ) : (
            <div className="ap-cards-wrap">
              <div className="ap-cards-grid">
                {filteredProducts.map((p) => (
                  <article key={p._id} className="ap-product-card">
                    <div className="ap-product-card-head">
                      <div className="ap-product">
                        <div className="ap-product-icon">
                          <span className="material-symbols-outlined">inventory_2</span>
                        </div>
                        <div>
                          <div className="ap-name">{p.name}</div>
                          {p.description ? <div className="ap-desc">{p.description}</div> : null}
                        </div>
                      </div>
                      <span className={`ap-status ${p.active ? "active" : "inactive"}`}>
                        <span className="ap-dot" />
                        {p.active ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div className="ap-tier-chips ap-card-meta">
                      <span className="ap-chip ap-chip-muted">
                        {(p.targetAudience || "vendor") === "vendor" ? "Vendor Product" : "Supplier Product"}
                      </span>
                      <span className="ap-chip ap-chip-muted">
                        {p.dispatchFormType === "paddy" ? "Paddy Format" : "Standard Form"}
                      </span>
                      {getProductWeightLabel(p) ? (
                        <span className="ap-chip ap-chip-muted">{getProductWeightLabel(p)}</span>
                      ) : (
                        <span className="ap-chip">{p.unit}</span>
                      )}
                      <span className="ap-chip ap-chip-muted">
                        GST {Number(p.gstPercent || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}%
                      </span>
                      <span className="ap-chip ap-chip-muted">
                        {(p.galleryImages || []).length} gallery image{(p.galleryImages || []).length === 1 ? "" : "s"}
                      </span>
                    </div>

                    <div className="ap-tier-chips ap-card-tiers">
                      {getPrimaryRate(p) === "" ? (
                        <span className="ap-chip ap-chip-muted">No tiers defined</span>
                      ) : (
                        <span className="ap-chip ap-chip-primary">Rate: Rs {getPrimaryRate(p)}</span>
                      )}
                    </div>

                    <div className="ap-gallery-grid">
                      {(p.galleryImages || []).length === 0 ? (
                        <div className="ap-gallery-empty">No gallery images yet.</div>
                      ) : (
                        (p.galleryImages || []).map((image) => (
                          <div key={image._id} className="ap-gallery-card">
                            <img src={resolveAssetUrl(image.imageUrl)} alt={image.caption || p.name} />
                            <div className="ap-gallery-card-body">
                              <div className="ap-gallery-card-headline">
                                <div className="ap-gallery-card-title">{image.caption || "Untitled image"}</div>
                                {image.isPrimary ? <span className="ap-chip ap-chip-primary">Front</span> : null}
                              </div>
                              <div className="ap-tier-chips">
                                {(image.packagingWeights || []).length === 0 ? (
                                  <span className="ap-chip ap-chip-muted">All packaging</span>
                                ) : (
                                  image.packagingWeights.map((weight) => (
                                    <span key={`${image._id}-${weight}`} className="ap-chip ap-chip-muted">
                                      {weight}
                                    </span>
                                  ))
                                )}
                              </div>
                              <div className="ap-gallery-card-actions">
                                <button
                                  className="ap-icon-btn"
                                  onClick={() => startGalleryEdit(p, image)}
                                  title="Edit image"
                                >
                                  <span className="material-symbols-outlined">edit</span>
                                </button>
                                <button
                                  className="ap-icon-btn"
                                  onClick={() => deleteGalleryImage(p._id, image._id)}
                                  title="Delete image"
                                >
                                  <span className="material-symbols-outlined">delete</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="ap-card-actions">
                      <button className="ap-icon-btn" onClick={() => startEdit(p)} title="Edit product">
                        <span className="material-symbols-outlined">edit_square</span>
                      </button>
                      <button
                        className="ap-icon-btn"
                        onClick={() => setGalleryForm((prev) => ({ ...defaultGalleryForm(), productId: p._id }))}
                        title="Upload image for this product"
                      >
                        <span className="material-symbols-outlined">add_photo_alternate</span>
                      </button>
                      <button
                        className={`ap-icon-btn ${p.active ? "" : "ap-icon-btn-success"}`}
                        onClick={() => toggleActive(p)}
                        title={p.active ? "Deactivate" : "Activate"}
                      >
                        <span className="material-symbols-outlined">
                          {p.active ? "block" : "check_circle"}
                        </span>
                      </button>
                      <button
                        className="ap-icon-btn"
                        onClick={() => deleteProduct(p)}
                        title="Delete"
                      >
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminProductCatalog;
